import cn from 'classnames'
import { safeTranslations } from 'legacy/lut/helpers/safeTranslations'
import { useMemo } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IS_SHOW_CY_ACTUALS_COLUMNS } from '../../config'

import { RETAIL_HEADER_KEY } from './config'
import { extractFromParentheses } from './helpers/extractFromParentheses'
import tr from './translations'

import styles from './RetaiCategoryHeaders.module.scss'

interface IProps {
  cat: string
  fyear: number
  valueType: ActualsValueTypes
}

export const RetailCategoryHeader = ({ cat, fyear, valueType }: IProps) => {
  /** ------------------------- Hooks ------------------------- */

  const t = useSafetyTranslation()

  /** ------------------------- Variables ------------------------- */

  const headerRow1Key = useMemo(() => `${RETAIL_HEADER_KEY}-header-row-1`, [])
  const headerRow2Key = useMemo(() => `${RETAIL_HEADER_KEY}-header-row-2`, [])
  const subCat = useMemo(() => extractFromParentheses(safeTranslations(cat, t)), [cat, t])
  const cy = useMemo(() => fyear % 100, [fyear])
  const py = useMemo(() => (fyear % 100) - 1, [fyear])
  const netNdp = useMemo(() => (valueType === ActualsValueTypes.Ndp ? 'Ndp' : 'Net'), [valueType])
  const replaceParams = useMemo(
    () => [
      { key: '{CurFYear}', value: cy.toString() },
      { key: '{PrevFYear}', value: py.toString() },
      { key: '{NetNdp}', value: netNdp.toString() },
      { key: '{SubCat}', value: subCat.toString() },
    ],
    [cy, netNdp, py, subCat]
  )

  /** ------------------------- Render ------------------------- */

  return (
    <>
      <tr key={headerRow1Key}>
        <th className={cn(styles.quarterCell, styles.colSeparator2)}>
          <div className={styles.quarterContainer}>&nbsp;</div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>&nbsp;</div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.tamGrowth} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.finActualsPy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.finGoalCy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.finYoY} replaceParams={replaceParams} />
          </div>
        </th>
        {IS_SHOW_CY_ACTUALS_COLUMNS && (
          <>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>
                <Translation text={tr.row1.finActualsCy} replaceParams={replaceParams} />
              </div>
            </th>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>
                <Translation text={tr.row1.finActualsYoY} replaceParams={replaceParams} />
              </div>
            </th>
          </>
        )}
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.sobActualsPy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.sobGoalCy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)}>
          <div className={styles.quarterContainer}>
            <Translation text={tr.row1.sobYoY} replaceParams={replaceParams} />
          </div>
        </th>
        {IS_SHOW_CY_ACTUALS_COLUMNS && (
          <>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>
                <Translation text={tr.row1.sobActualsCy} replaceParams={replaceParams} />
              </div>
            </th>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>
                <Translation text={tr.row1.sobActualsYoY} replaceParams={replaceParams} />
              </div>
            </th>
          </>
        )}
      </tr>

      <tr key={headerRow2Key}>
        <th className={styles.titleCell}>
          <Translation text={tr.row2.code} replaceParams={replaceParams} />
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapSubCatCell}>
            <Translation text={tr.row2.subCat} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.tamGrowth} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.finActualsPy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.finGoalCy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.finYoY} replaceParams={replaceParams} />
          </div>
        </th>
        {IS_SHOW_CY_ACTUALS_COLUMNS && (
          <>
            <th className={cn(styles.tableCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <Translation text={tr.row2.finActualsCy} replaceParams={replaceParams} />
              </div>
            </th>
            <th className={cn(styles.tableCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <Translation text={tr.row2.finActualsYoY} replaceParams={replaceParams} />
              </div>
            </th>
          </>
        )}
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.sobActualsPy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.sobGoalCy} replaceParams={replaceParams} />
          </div>
        </th>
        <th className={cn(styles.subCatCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <Translation text={tr.row2.sobYoY} replaceParams={replaceParams} />
          </div>
        </th>
        {IS_SHOW_CY_ACTUALS_COLUMNS && (
          <>
            <th className={cn(styles.tableCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <Translation text={tr.row2.sobActualsCy} replaceParams={replaceParams} />
              </div>
            </th>
            <th className={cn(styles.subCatCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <Translation text={tr.row2.sobActualsYoY} replaceParams={replaceParams} />
              </div>
            </th>
          </>
        )}
      </tr>
    </>
  )
}
